import React, { useState } from 'react';
import { Calendar, MapPin, Users, Camera, X, ChevronLeft, ChevronRight } from 'lucide-react';
import PageHero from './PageHero';

interface ExcursionVisit {
  id: string;
  title: string;
  location: string;
  date: string;
  description: string;
  images: string[];
  participantCount: number;
  category: 'Science' | 'History' | 'Nature' | 'Culture' | 'Technology' | 'Art';
  highlights: string[];
}

interface ExcursionGalleryProps {
  visits: ExcursionVisit[];
  title?: string;
  subtitle?: string;
  heroGradientColors?: {
    from: string;
    via?: string;
    to: string;
  };
}

const ExcursionGallery: React.FC<ExcursionGalleryProps> = ({
  visits,
  title = "Educational Excursions",
  subtitle = "Exploring the world beyond classroom walls",
  heroGradientColors = {
    from: 'from-emerald-600',
    via: 'via-teal-600',
    to: 'to-cyan-600'
  }
}) => {
  const [selectedVisit, setSelectedVisit] = useState<ExcursionVisit | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [activeFilter, setActiveFilter] = useState<string>('All');

  const categories = ['All', 'Science', 'History', 'Nature', 'Culture', 'Technology', 'Art'];
  
  const getCategoryColor = (category: string) => {
    const colors = {
      Science: 'bg-blue-100 text-blue-800',
      History: 'bg-amber-100 text-amber-800',
      Nature: 'bg-green-100 text-green-800',
      Culture: 'bg-purple-100 text-purple-800',
      Technology: 'bg-gray-100 text-gray-800',
      Art: 'bg-pink-100 text-pink-800'
    };
    return colors[category as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const filteredVisits = activeFilter === 'All' 
    ? visits 
    : visits.filter(visit => visit.category === activeFilter);

  const openModal = (visit: ExcursionVisit, imageIndex: number = 0) => {
    setSelectedVisit(visit);
    setSelectedImageIndex(imageIndex);
  };

  const closeModal = () => {
    setSelectedVisit(null);
    setSelectedImageIndex(0);
  };

  const nextImage = () => {
    if (selectedVisit) {
      setSelectedImageIndex((prev) => 
        prev === selectedVisit.images.length - 1 ? 0 : prev + 1
      );
    }
  };

  const prevImage = () => {
    if (selectedVisit) {
      setSelectedImageIndex((prev) => 
        prev === 0 ? selectedVisit.images.length - 1 : prev - 1
      );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50">
      <PageHero
        title={title}
        subtitle={subtitle}
        gradientColors={heroGradientColors}
        titleGradient={{
          from: 'from-white',
          to: 'to-emerald-100'
        }}
        subtitleColor="text-emerald-100"
      />

      <div className="container mx-auto px-4 py-12">
        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setActiveFilter(category)}
              className={`px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                activeFilter === category
                  ? 'bg-blue-600 text-white shadow-lg scale-105'
                  : 'bg-white text-gray-700 hover:bg-blue-50 hover:text-blue-600 shadow-md'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredVisits.map((visit) => (
            <div
              key={visit.id}
              className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 hover:scale-105"
            >
              {/* Main Image */}
              <div className="relative h-64 overflow-hidden">
                <img
                  src={visit.images[0] || "/api/placeholder/400/300"}
                  alt={visit.title}
                  className="w-full h-full object-cover transition-transform duration-300 hover:scale-110 cursor-pointer"
                  onClick={() => openModal(visit, 0)}
                />
                <div className="absolute top-4 left-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getCategoryColor(visit.category)}`}>
                    {visit.category}
                  </span>
                </div>
                <div className="absolute bottom-4 right-4 bg-black/70 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm flex items-center gap-1">
                  <Camera size={14} />
                  {visit.images.length}
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2">
                  {visit.title}
                </h3>
                
                <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
                  <div className="flex items-center gap-1">
                    <MapPin size={16} />
                    <span>{visit.location}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Calendar size={16} />
                    <span>{visit.date}</span>
                  </div>
                </div>

                <p className="text-gray-700 text-sm mb-4 line-clamp-3">
                  {visit.description}
                </p>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1 text-sm text-gray-600">
                    <Users size={16} />
                    <span>{visit.participantCount} participants</span>
                  </div>
                  <button
                    onClick={() => openModal(visit)}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200"
                  >
                    View Gallery
                  </button>
                </div>

                {/* Thumbnail Images */}
                {visit.images.length > 1 && (
                  <div className="flex gap-2 mt-4 overflow-x-auto">
                    {visit.images.slice(1, 4).map((image, index) => (
                      <img
                        key={index}
                        src={image || "/api/placeholder/100/80"}
                        alt={`${visit.title} ${index + 2}`}
                        className="w-16 h-12 object-cover rounded-lg cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={() => openModal(visit, index + 1)}
                      />
                    ))}
                    {visit.images.length > 4 && (
                      <div
                        className="w-16 h-12 bg-gray-200 rounded-lg flex items-center justify-center cursor-pointer hover:bg-gray-300 transition-colors"
                        onClick={() => openModal(visit)}
                      >
                        <span className="text-xs font-medium text-gray-600">
                          +{visit.images.length - 4}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Modal */}
        {selectedVisit && (
          <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl w-full w-full max-h-[90vh] overflow-hidden">
              <div className="flex">
                {/* Image Section */}
                <div className="flex-1 relative">
                  <img
                    src={selectedVisit.images[selectedImageIndex] || "/api/placeholder/800/600"}
                    alt={selectedVisit.title}
                    className="w-full h-96 lg:h-[600px] object-cover"
                  />
                  
                  {/* Navigation Arrows */}
                  {selectedVisit.images.length > 1 && (
                    <>
                      <button
                        onClick={prevImage}
                        className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
                      >
                        <ChevronLeft size={24} />
                      </button>
                      <button
                        onClick={nextImage}
                        className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
                      >
                        <ChevronRight size={24} />
                      </button>
                    </>
                  )}

                  {/* Image Counter */}
                  <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/70 text-white px-3 py-1 rounded-full text-sm">
                    {selectedImageIndex + 1} / {selectedVisit.images.length}
                  </div>
                </div>

                {/* Details Section */}
                <div className="w-80 p-6 overflow-y-auto">
                  <div className="flex items-start justify-between mb-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getCategoryColor(selectedVisit.category)}`}>
                      {selectedVisit.category}
                    </span>
                    <button
                      onClick={closeModal}
                      className="text-gray-500 hover:text-gray-700 p-1"
                    >
                      <X size={24} />
                    </button>
                  </div>

                  <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    {selectedVisit.title}
                  </h2>

                  <div className="space-y-3 mb-6">
                    <div className="flex items-center gap-2 text-gray-600">
                      <MapPin size={18} />
                      <span>{selectedVisit.location}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <Calendar size={18} />
                      <span>{selectedVisit.date}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <Users size={18} />
                      <span>{selectedVisit.participantCount} participants</span>
                    </div>
                  </div>

                  <p className="text-gray-700 mb-6 leading-relaxed">
                    {selectedVisit.description}
                  </p>

                  {selectedVisit.highlights.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-3">Highlights</h3>
                      <ul className="space-y-2">
                        {selectedVisit.highlights.map((highlight, index) => (
                          <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
                            <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                            {highlight}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Thumbnail Navigation */}
                  {selectedVisit.images.length > 1 && (
                    <div className="mt-6">
                      <h4 className="font-medium text-gray-900 mb-3">All Photos</h4>
                      <div className="grid grid-cols-4 gap-2">
                        {selectedVisit.images.map((image, index) => (
                          <img
                            key={index}
                            src={image || "/api/placeholder/100/80"}
                            alt={`${selectedVisit.title} ${index + 1}`}
                            className={`w-full h-16 object-cover rounded-lg cursor-pointer transition-all ${
                              selectedImageIndex === index 
                                ? 'ring-2 ring-blue-600 opacity-100' 
                                : 'opacity-70 hover:opacity-100'
                            }`}
                            onClick={() => setSelectedImageIndex(index)}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Example usage with sample data
const ExampleUsage = () => {
  const sampleVisits: ExcursionVisit[] = [
    {
      id: '1',
      title: 'National Science Museum Discovery',
      location: 'Delhi Science Museum',
      date: 'March 15, 2024',
      description: 'Students explored interactive exhibits on physics, chemistry, and biology, enhancing their understanding through hands-on experiments and demonstrations.',
      images: ['/api/placeholder/400/300', '/api/placeholder/400/300', '/api/placeholder/400/300'],
      participantCount: 45,
      category: 'Science',
      highlights: [
        'Interactive physics experiments',
        'Planetarium show experience',
        'Robotics demonstration',
        'Students conducted live experiments'
      ]
    },
    {
      id: '2',
      title: 'Red Fort Historical Expedition',
      location: 'Red Fort, Delhi',
      date: 'February 28, 2024',
      description: 'A journey through Mughal history, exploring the architectural marvels and learning about India\'s rich cultural heritage.',
      images: ['/api/placeholder/400/300', '/api/placeholder/400/300'],
      participantCount: 60,
      category: 'History',
      highlights: [
        'Guided tour by historian',
        'Architecture photography session',
        'Cultural heritage workshop',
        'Historical artifact examination'
      ]
    },
    {
      id: '3',
      title: 'Botanical Garden Nature Study',
      location: 'National Botanical Garden',
      date: 'April 10, 2024',
      description: 'Environmental education through direct observation of diverse plant species and ecosystem interactions.',
      images: ['/api/placeholder/400/300', '/api/placeholder/400/300', '/api/placeholder/400/300', '/api/placeholder/400/300'],
      participantCount: 35,
      category: 'Nature',
      highlights: [
        'Plant identification workshop',
        'Ecosystem study activities',
        'Photography competition',
        'Environmental conservation talk'
      ]
    }
  ];

  return <ExcursionGallery visits={sampleVisits} />;
};

export default ExampleUsage;